import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, AlertTriangle, RotateCw } from "lucide-react";
import SEO from "@/components/app/SEO";

export default function DirectoryPage() {
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState(sp.get("q") || "");
  const [cat, setCat] = useState(sp.get("cat") || "");
  const [orgs, setOrgs] = useState([]);
  const [cats, setCats] = useState([]);
  const [total, setTotal] = useState(null); // null = unknown / not loaded yet
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    api.get("/categories").then((r) => setCats((r.data || []).filter((c) => !c.parent_slug))).catch(() => setCats([]));
  }, []);

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (cat) params.set("category", cat);
      params.set("limit", "100");
      const { data } = await api.get(`/organizations?${params.toString()}`);
      setOrgs(Array.isArray(data.items) ? data.items : []);
      setTotal(typeof data.total === "number" ? data.total : (data.items || []).length);
    } catch (e) {
      setError(e?.response?.data?.detail || "L'annuaire est temporairement indisponible. Veuillez réessayer dans quelques instants.");
      setOrgs([]);
      setTotal(null);
    } finally {
      setLoading(false);
    }
  }, [q, cat]);

  useEffect(() => {
    fetchOrgs();
    const upd = new URLSearchParams();
    if (q) upd.set("q", q);
    if (cat) upd.set("cat", cat);
    setSp(upd, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, cat, reloadKey]);

  const displayTotal = total == null ? "…" : total;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
      <SEO title="Annuaire de résiliation" description="Annuaire vérifié des organismes français : adresses de résiliation, méthodes, préavis, justificatifs." canonical="/annuaire" />
      <div className="mb-8">
        <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Annuaire de résiliation</p>
        <h1 className="text-4xl font-bold tracking-tight mb-4">Trouvez votre organisme</h1>
        <p className="text-muted-foreground max-w-2xl">Adresses, méthodes de résiliation, préavis, justificatifs — vérifiés et structurés.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-8">
        <aside className="md:sticky md:top-24 h-fit">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">Catégories</div>
          <button
            onClick={() => setCat("")}
            data-testid="cat-filter-all"
            className={`w-full text-left px-3 py-2 rounded-md text-sm mb-1 transition-colors duration-200 ${!cat ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
          >
            Toutes ({displayTotal})
          </button>
          {cats.map((c) => (
            <button
              key={c.slug}
              onClick={() => setCat(c.slug)}
              data-testid={`cat-filter-${c.slug}`}
              className={`w-full text-left px-3 py-2 rounded-md text-sm mb-1 transition-colors duration-200 ${cat === c.slug ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
            >
              {c.name}
            </button>
          ))}
        </aside>

        <div>
          <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-2 pl-4 mb-6">
            <Search className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <label htmlFor="dir-q" className="sr-only">Rechercher un organisme</label>
            <Input
              id="dir-q"
              data-testid="directory-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un organisme (Néoliane, Heomi, SFR, EDF…)"
              className="border-0 shadow-none focus-visible:ring-0"
            />
          </div>

          {!loading && !error && (
            <div className="text-sm text-muted-foreground mb-4 font-mono" data-testid="directory-total">{total} organisme{total > 1 ? "s" : ""}</div>
          )}

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="directory-loading">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="card-elevated p-5">
                  <div className="animate-pulse space-y-3">
                    <div className="h-4 bg-secondary rounded w-3/5" />
                    <div className="h-3 bg-secondary rounded w-full" />
                    <div className="h-3 bg-secondary rounded w-4/5" />
                    <div className="flex gap-1"><div className="h-4 w-14 bg-secondary rounded" /><div className="h-4 w-14 bg-secondary rounded" /></div>
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="card-elevated p-10 text-center" data-testid="directory-error">
              <AlertTriangle className="h-8 w-8 text-orange-500 mx-auto mb-3" aria-hidden="true" />
              <p className="mb-4 text-muted-foreground">{error}</p>
              <Button onClick={() => setReloadKey((k) => k + 1)} data-testid="directory-retry-btn"><RotateCw className="h-4 w-4 mr-2" />Réessayer</Button>
            </div>
          ) : orgs.length === 0 ? (
            <div className="card-elevated p-10 text-center" data-testid="directory-empty">
              <p className="mb-2 font-semibold">Aucun organisme ne correspond à votre recherche.</p>
              <p className="mb-6 text-sm text-muted-foreground">Vérifiez l'orthographe ou proposez l'ajout de cet organisme.</p>
              <Button asChild data-testid="suggest-org-btn"><Link to="/resilier">Proposer un organisme</Link></Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {orgs.map((o) => (
                <Link key={o.slug} to={`/organisme/${o.slug}`} data-testid={`directory-org-${o.slug}`} className="card-elevated p-5">
                  <div className="flex items-start justify-between mb-2">
                    <div className="font-semibold">{o.name}</div>
                    {o.trust_level === "verified" && (
                      <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300 border-0">Vérifié</Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground line-clamp-2 mb-3">{o.description}</div>
                  <div className="flex flex-wrap gap-1">
                    {(o.termination_methods || []).slice(0, 3).map((m) => (
                      <Badge key={m} variant="outline" className="text-[10px]">{m.replace("_", " ")}</Badge>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
